import {
  useEffect,
  useState,
} from 'react';

import type {
  PublicationInboxItem,
} from '@/hooks/usePublicationInbox';

import {
  usePublicationInboxLinking,
  type PublicationLinkClient,
} from '@/hooks/usePublicationInboxLinking';

type Props = {
  item: PublicationInboxItem | null;
  onChanged: () => Promise<void>;
};

export default function PublicationInboxLinkPanel({
  item,
  onChanged,
}: Props) {
  const {
    loadClients,
    createCaseFromPublication,
  } = usePublicationInboxLinking();

  const [
    clients,
    setClients,
  ] =
    useState<
      PublicationLinkClient[]
    >([]);

  const [
    clientId,
    setClientId,
  ] =
    useState('');

  const [
    isLoadingClients,
    setIsLoadingClients,
  ] =
    useState(false);

  const [
    isSaving,
    setIsSaving,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState<string | null>(
      null
    );

  const [
    message,
    setMessage,
  ] =
    useState<string | null>(
      null
    );

  const isUnlinked =
    Boolean(
      item &&
      !item.context.caseId
    );

  useEffect(() => {
    setClientId('');
    setError(null);
    setMessage(null);

    if (!isUnlinked) {
      setClients([]);
      return;
    }

    let active = true;

    const run =
      async () => {
        setIsLoadingClients(
          true
        );

        try {
          const loaded =
            await loadClients();

          if (active) {
            setClients(loaded);
          }
        } catch (
          loadError
        ) {
          if (active) {
            setError(
              loadError instanceof Error
                ? loadError.message
                : 'Não foi possível carregar os clientes.'
            );
          }
        } finally {
          if (active) {
            setIsLoadingClients(
              false
            );
          }
        }
      };

    void run();

    return () => {
      active = false;
    };
  }, [
    item?.id,
    isUnlinked,
    loadClients,
  ]);

  if (
    !item ||
    !isUnlinked
  ) {
    return null;
  }

  const processNumber =
    item.publication
      .processNumber ?? '';

  const save =
    async () => {
      setIsSaving(true);
      setError(null);
      setMessage(null);

      try {
        const result =
          await createCaseFromPublication({
            clientId,
            processNumber,
            tribunal:
              item.publication
                .tribunal,
          });

        const matched =
          result.reconciliation
            ?.matched ?? 0;

        const scanned =
          result.reconciliation
            ?.scanned ?? 0;

        setMessage(
          scanned > 0
            ? `Caso criado. ${matched} de ${scanned} publicação(ões) deste processo foram vinculadas automaticamente.`
            : 'Caso criado. Não havia outra publicação histórica elegível para reconciliação.'
        );

        await onChanged();
      } catch (
        saveError
      ) {
        setError(
          saveError instanceof Error
            ? saveError.message
            : 'Não foi possível criar e vincular o caso.'
        );
      } finally {
        setIsSaving(false);
      }
    };

  return (
    <section className="space-y-3 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-800">
          Publicação sem vínculo
        </p>
        <p className="mt-1 text-xs leading-5 text-amber-700">
          Nenhum caso cadastrado corresponde a este processo. Selecione o cliente e crie o caso. As publicações do mesmo CNJ serão reconciliadas automaticamente.
        </p>
      </div>

      <div className="rounded-lg border border-amber-200 bg-white p-3 text-xs text-foreground-700">
        <span className="font-semibold">
          Processo:
        </span>{' '}
        {processNumber || '-'}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {error}
        </div>
      )}

      {message && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
          {message}
        </div>
      )}

      <label className="block space-y-1">
        <span className="text-xs font-medium text-foreground-600">
          Cliente
        </span>

        <select
          value={clientId}
          onChange={(event) =>
            setClientId(
              event.target.value
            )
          }
          disabled={
            isLoadingClients ||
            isSaving
          }
          className="w-full rounded-lg border border-background-200 bg-white px-3 py-2 text-sm text-foreground-700"
        >
          <option value="">
            {isLoadingClients
              ? 'Carregando clientes...'
              : 'Selecione o cliente'}
          </option>

          {clients.map(
            (client) => (
              <option
                key={client.id}
                value={client.id}
              >
                {client.name}
              </option>
            )
          )}
        </select>
      </label>

      <button
        type="button"
        onClick={() =>
          void save()
        }
        disabled={
          isSaving ||
          isLoadingClients ||
          !clientId ||
          !processNumber
        }
        className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSaving
          ? 'Criando e vinculando...'
          : 'Criar caso e vincular processo'}
      </button>

      <p className="text-[11px] leading-4 text-amber-700">
        O cliente não é inferido automaticamente pelo nome das partes. A confirmação humana evita associação de processo à pessoa errada.
      </p>
    </section>
  );
}